/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import { useMediaQuery } from 'react-responsive';

import styles from './Pagination.module.scss';

const VISIBLE_LARGE = 5;
const VISIBLE_SMALL = 3;

/**
 * This is the Pagination component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Pagination({
  standalone,
  baseUrl,
  parameter,
  total,
  current: initialCurrent,
  onClick,
  labels,
}) {
  const useLargeLayout = useMediaQuery({
    query: `(min-width: ${styles['page-width-medium-phone']})`,
  });

  const visible = useLargeLayout ? VISIBLE_LARGE : VISIBLE_SMALL;

  const current = clamp(initialCurrent, 1, total);

  const from = Math.max(
    1,
    Math.min(total - visible + 1, current - Math.floor(visible / 2))
  );
  const to = Math.max(
    Math.min(total, visible),
    Math.min(total, current + Math.floor(visible / 2))
  );

  const Link = ({ label, page, className, disabled, children }) => {
    if (standalone) {
      const href = `${baseUrl}?${parameter}=${page}`;
      return (
        <a
          href={disabled ? null : href}
          className={classNames(
            styles.link,
            'link',
            'link-current',
            'link-reversed',
            className
          )}
          data-page={page}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        className={classNames(
          styles.link,
          'link',
          'link-current',
          'link-reversed',
          className
        )}
        disabled={disabled}
        data-page={page}
        onClick={() => onClick(page)}
      >
        {children}
      </button>
    );
  };

  const pages = range(from, to + 1);

  if (total < 2) {
    return ull;
  }

  return (
    <div className={styles.main}>
      <Link
        className={styles.prev}
        page={Math.max(1, current - 1)}
        disabled={current === 1}
      >
        <span>{labels.btnPrevPage}</span>
      </Link>
      <div className={styles.pages}>
        {pages.map((page) => (
          <Link
            key={`page_${page}`}
            page={page}
            className={classNames({
              [styles.current]: page === current,
            })}
            disabled={page === current}
          >
            {page}
          </Link>
        ))}
      </div>
      <Link
        className={styles.next}
        page={Math.min(total, current + 1)}
        disabled={current === total}
      >
        <span>{labels.btnNextPage}</span>
      </Link>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Pagination.propTypes = {
  standalone: PropTypes.bool,
  baseUrl: PropTypes.string,
  parameter: PropTypes.string,
  total: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  labels: PropTypes.shape({
    btnPrevPage: PropTypes.string,
    btnNextPage: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Pagination.defaultProps = {
  standalone: false,
  baseUrl: null,
  parameter: null,
  onClick: () => {},
  labels: {
    btnPrevPage: 'Forrige side',
    btnNextPage: 'Neste side',
  },
  current: 1,
};

export default Pagination;
